import { AfterContentInit, Component, ContentChildren, ElementRef, EventEmitter, HostListener, Input, Output, QueryList, Signal, ViewChild, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TabPaneComponent } from './tab-pane.component';

@Component({
  selector: 'app-tabs',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tabs.component.html',
  styleUrls: ['./tabs.component.scss']
})
export class TabsComponent implements AfterContentInit {
  @ViewChild('scrollContainer', { static: true }) scrollContainer?: ElementRef<HTMLDivElement>;
  canScrollLeft = false;
  canScrollRight = false;
  private scrollAmount = 0; // will compute based on container width
  @Input() defaultActiveId?: string;
  @Output() activeChange = new EventEmitter<string | undefined>();

  @ContentChildren(TabPaneComponent) paneList!: QueryList<TabPaneComponent>;

  // Internal reactive state for active id
  activeId: Signal<string | undefined> = signal<string | undefined>(undefined);

  get panes(): TabPaneComponent[] {
    return this.paneList?.toArray() ?? [];
  }

  ngAfterContentInit(): void {
    // Initialize active tab
    const firstEnabled = this.panes.find(p => !p.disabled);
    const initial = this.defaultActiveId && this.panes.some(p => p.id === this.defaultActiveId && !p.disabled)
      ? this.defaultActiveId
      : firstEnabled?.id;
    (this.activeId as any).set(initial);
    this.updatePaneVisibility();
    this.activeChange.emit(initial);
    setTimeout(() => { this.recomputeScrollAmount(); this.updateScrollButtons(); this.scrollActiveIntoView(); });

    // React to dynamic changes (if panes change)
    this.paneList.changes.subscribe(() => {
      // Keep current if still valid; otherwise pick first enabled
      const current = this.activeId();
      const stillValid = current && this.panes.some(p => p.id === current && !p.disabled);
      const next = stillValid ? current : (this.panes.find(p => !p.disabled)?.id);
      (this.activeId as any).set(next);
      this.updatePaneVisibility();
      this.activeChange.emit(next);
      setTimeout(() => { this.recomputeScrollAmount(); this.updateScrollButtons(); this.scrollActiveIntoView(); });
    });
  }

  select(pane: TabPaneComponent) {
    if (pane.disabled) return;
    (this.activeId as any).set(pane.id);
    this.updatePaneVisibility();
    this.activeChange.emit(pane.id);
    this.scrollActiveIntoView();
  }

  private updatePaneVisibility() {
    setTimeout(() => {
      const current = this.activeId();
      for (const p of this.panes) {
        p.hidden = p.id !== current;
      }
    });
  }

  @HostListener('window:resize')
  onResize() {
    this.recomputeScrollAmount();
    this.updateScrollButtons();
  }

  onScroll() {
    this.updateScrollButtons();
  }

  private updateScrollButtons() {
    const el = this.scrollContainer?.nativeElement;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    this.canScrollLeft = scrollLeft > 0;
    this.canScrollRight = Math.ceil(scrollLeft + clientWidth) < scrollWidth;
  }

  private recomputeScrollAmount() {
    const el = this.scrollContainer?.nativeElement;
    if (!el) return;
    this.scrollAmount = Math.round(el.clientWidth * 0.8);
  }

  scrollBy(direction: 'left' | 'right') {
    const el = this.scrollContainer?.nativeElement;
    if (!el) return;
    const amount = this.scrollAmount || Math.round(el.clientWidth * 0.8);
    const delta = direction === 'left' ? -amount : amount;
    el.scrollBy({ left: delta, behavior: 'smooth' });
    // Update after animation frame
    setTimeout(() => this.updateScrollButtons(), 300);
  }

  private scrollActiveIntoView() {
    const el = this.scrollContainer?.nativeElement;
    if (!el) return;
    const current = this.activeId();
    if (!current) return;
    // Find the active button by data-id attribute
    const btn: HTMLElement | null = el.querySelector(`[data-tab-id="${current}"]`);
    if (!btn) return;
    const btnRect = btn.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    if (btnRect.left < elRect.left) {
      el.scrollBy({ left: btnRect.left - elRect.left, behavior: 'smooth' });
    } else if (btnRect.right > elRect.right) {
      el.scrollBy({ left: btnRect.right - elRect.right, behavior: 'smooth' });
    }
    setTimeout(() => this.updateScrollButtons(), 300);
  }
}
